import { z } from "zod";
import {
  DomainModelMetaExtendedSchema,
  DomainModelMetaSchema,
  DomainModelSchema,
  type TypeOfObjectValues,
} from "../util/index.ts";
import { AssetTypeSchema } from "./AssetSchemas.ts";
import { AttributeDataType } from "./AttributeSchemas.ts";
import { FileUploadedSchema } from "./FileSchemas.ts";
import { FormSubmissionStatus } from "./FormSubmissionSchemas.shared.ts";
import { FormSubmissionAnswerSchema } from "./FormSubmissionSchemas.ts";
import {
  WORKFLOW_VARNAME_REGEX,
  WorkflowConfigSchema,
} from "./WorkflowConfigSchemas.ts";
import { DatasetSubmissionTriggerMetadataSchema } from "./WorkflowTriggerMetadataSchemas.ts";

export const WorkflowStatus = {
  IN_PROGRESS: "in_progress",
  COMPLETE: "complete",
  OVERDUE: "overdue",
  DISMISSED: "dismissed",
  MISSED: "missed",
  ERROR: "error",
} as const;
export type WorkflowStatusType = TypeOfObjectValues<typeof WorkflowStatus>;

export const WorkflowStepState = {
  PENDING: "pending",
  FINISHED: "finished",
  SKIPPED: "skipped",
  FAILED: "failed",
} as const;
export type WorkflowStepStateType = TypeOfObjectValues<
  typeof WorkflowStepState
>;

export const WorkflowDatasetTriggerSchema = z.object({
  workflow_id: z.string().uuid(),
  dataset_id: z.string().uuid(),
  transaction_id: z.string().uuid(),
  metadata: DatasetSubmissionTriggerMetadataSchema,
});

export type WorkflowDatasetTriggerType = z.infer<
  typeof WorkflowDatasetTriggerSchema
>;

const ChoiceSchema = z.object({
  id: z.string().max(64).min(1),
  name: z.string().max(256),
  description: z.string().max(1024),
  next: z.string().optional(),
  end: z.literal(true).optional(),
});
export type ChoiceType = z.infer<typeof ChoiceSchema>;

export const WorkflowVariableValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.array(z.string()),
  z.array(z.number()),
  FileUploadedSchema,
  z.array(FileUploadedSchema),
  z.record(z.string(), z.unknown()),
  z.array(z.record(z.string(), z.unknown())),
]);
export type WorkflowVariableValueType = z.infer<
  typeof WorkflowVariableValueSchema
>;

export const WorkflowSchema = z
  .object({
    name: z.string(),
    description: z.string(),
    company_id: z.string().uuid(),
    workflow_template_id: z.string().uuid(),
    workflow_template: z.object({
      id: z.string(),
      name: z.string(),
      description: z.string(),
      workflow_category: z.object({
        id: z.string(),
        name: z.string(),
      }),
    }),
    status: z.nativeEnum(WorkflowStatus),
    due_date: z.date().or(z.string().datetime()),
    step_states: z.record(z.string(), z.nativeEnum(WorkflowStepState)),
    facility_id: z.string().nullish(),
    asset_id: z.string().nullish(),
    time_period: z.string().nullish(),
    duration: z.number().nullish(),
    facility: z
      .object({
        id: z.string(),
        name: z.string(),
      })
      .nullish(),
    asset: z
      .object({ id: z.string(), name: z.string(), asset_type: AssetTypeSchema })
      .nullish(),
    config: WorkflowConfigSchema,
    variables: z.record(z.string(), WorkflowVariableValueSchema.nullable()),
    asset_trigger: z
      .object({
        metadata: z.object({
          asset_type: AssetTypeSchema,
          asset_id: z.string(),
          facility_id: z.string().optional(),
          action: z.string(),
        }),
      })
      .nullable()
      .optional(),
    form_trigger: z
      .object({
        workflow_id: z.string(),
        form_submission_id: z.string(),
        form_submission_version: z.number().int(),
        metadata: FormSubmissionAnswerSchema,
      })
      .nullable()
      .optional(),
    workflow_trigger: z
      .object({
        triggered_workflow_id: z.string(),
        triggering_workflow_id: z.string(),
        triggering_workflow_name: z.string().optional(),
        metadata: z.object({
          triggering_workflow_id: z.string(),
          triggering_workflow_template_id: z.string(),
        }),
      })
      .nullable()
      .optional(),
    dataset_trigger: WorkflowDatasetTriggerSchema.nullable().optional(),
    dismissal_reason: z.string().nullable().optional(),
    dismissed_by: z.string().uuid().nullable().optional(),
    dismissed_at: z.date().or(z.string().datetime()).nullable().optional(),
  })
  // Merge id, created_at, updated_at, created_by, updated_by:
  .merge(DomainModelSchema)
  .merge(DomainModelMetaSchema)
  .merge(DomainModelMetaExtendedSchema);
export type WorkflowType = z.infer<typeof WorkflowSchema>;

export const WorkflowFormSubmissionSchema = z
  .object({
    created_by: z.string(),
    form_schema_id: z.string(),
    form_schema_version: z.number(),
    status: z.enum([
      FormSubmissionStatus.DRAFT,
      FormSubmissionStatus.INVALIDATED,
      FormSubmissionStatus.PENDING,
      FormSubmissionStatus.SUBMITTED,
      FormSubmissionStatus.VALIDATED,
    ]),
    form_schema: z.object({
      id: z.string(),
      name: z.string(),
      form_category: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .nullable(),
    }),
    associated_tasks: z.array(
      z.object({
        workflow_task_name: z.string(),
        workflow_task_id: z.string(),
        form_submission_id: z.string(),
      })
    ),
    associated_actions: z.array(
      z.object({
        workflow_action_name: z.string(),
        workflow_action_id: z.string(),
        form_submission_id: z.string(),
      })
    ),
    is_workflow_trigger: z.boolean(),
  })
  .merge(DomainModelSchema)
  .merge(DomainModelMetaSchema);
export type WorkflowFormSubmissionType = z.infer<
  typeof WorkflowFormSubmissionSchema
>;

const triggerWorkflowVariableDate = z.object({
  data_type: z.literal(AttributeDataType.DATE),
  format: z.enum(["YYYYMM", "YYYY", "YYYY-MM-DD", "ISO"]),
  value: z.string(),
});

const triggerWorkflowVariableDateTime = z.object({
  data_type: z.literal(AttributeDataType.DATE_TIME),
  format: z.literal("ISO"),
  value: z.string(),
});

const triggerWorkflowVariableString = z.object({
  data_type: z.literal(AttributeDataType.STRING),
  value: z.string(),
});

const triggerWorkflowVariableNumber = z.object({
  data_type: z.literal(AttributeDataType.NUMBER),
  value: z.number(),
});

const triggerWorkflowVariableInteger = z.object({
  data_type: z.literal(AttributeDataType.INTEGER),
  value: z.number().int(),
});

const triggerWorkflowVariableBoolean = z.object({
  data_type: z.literal(AttributeDataType.BOOLEAN),
  value: z.boolean(),
});

const triggerWorkflowVariablePickList = z.object({
  data_type: z.literal(AttributeDataType.PICK_LIST),
  value: z.string(),
});

const triggerWorkflowVariableMultiPickList = z.object({
  data_type: z.literal(AttributeDataType.MULTI_PICK_LIST),
  value: z.array(z.string()),
});

const triggerWorkflowVariableLookup = z.object({
  data_type: z.literal(AttributeDataType.LOOKUP),
  value: z.string(),
});

export const TriggerWorkflowVariableSchema = z.discriminatedUnion("data_type", [
  triggerWorkflowVariableDate,
  triggerWorkflowVariableDateTime,
  triggerWorkflowVariableString,
  triggerWorkflowVariableNumber,
  triggerWorkflowVariableInteger,
  triggerWorkflowVariableBoolean,
  triggerWorkflowVariablePickList,
  triggerWorkflowVariableMultiPickList,
  triggerWorkflowVariableLookup,
]);

export const TriggerWorkflowVariablesSchema = z.record(
  z.string(),
  z.string().or(TriggerWorkflowVariableSchema)
);
export type TriggerWorkflowVariablesType = z.infer<
  typeof TriggerWorkflowVariablesSchema
>;

export const TriggerWorkflowSchema = WorkflowSchema.pick({
  workflow_template_id: true,
  time_period: true,
  duration: true,
}).merge(
  z.object({
    asset_id: z.string().optional(),
    variables: TriggerWorkflowVariablesSchema.optional(),
  })
);
export type TriggerWorkflowType = z.infer<typeof TriggerWorkflowSchema>;

export const TriggerWorkflowFormSchema = z.object({
  asset_ids: z.array(z.string()),
  has_due_date: z.boolean(),
  time_period: z.string(),
  duration: z.number(),
  has_initial_variables: z.boolean(),
  initial_variables: z.record(
    z.string(),
    z
      .object({
        from: z.date(),
        to: z.date(),
      })
      .or(z.date())
      .or(z.string())
      .or(z.array(z.string()))
      .or(z.number())
      .or(z.boolean())
  ),
});
export type TriggerWorkflowFormType = z.infer<typeof TriggerWorkflowFormSchema>;

export const validateWorkflowVariableWithRegex = (value: string) =>
  WORKFLOW_VARNAME_REGEX.test(value);
